import { FC, useEffect } from 'react';
import styles from './WizardStart.module.scss';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLoggedinUser, useService } from '@hooks';
import { electrificationWizardService, notificationService, simulationService } from '@services';
import { CUSTOM_THEME, RxUtils, Tr } from '@utils';
import { Button, Stack, Typography } from '@mui/material';
import { RouterLinkButton } from '@components/common';

export const WizardStart: FC = () => {
	const [queryParams] = useSearchParams();
	const navigate = useNavigate();
	const loc = useLocation();
	const user = useLoggedinUser();
	const selectedYear = +(queryParams.get('year') || new Date().getFullYear());
	const { t } = useTranslation('portal');
	const { data: simulation } = useService(() => simulationService.getLatest());
	const userSellVehicles = user?.isDealer(user?.role);
	const userSellInfra = user?.isChargingPartner(user?.role);

	const isAutomaticSuggestionAvailable = () => {
		if (userSellVehicles) {
			return !!(selectedYear && simulation?.savings?.yearlySavings);
		} else if (userSellInfra) {
			return true;
		} else {
			return false; // TODO: what happens
		}
	};

	const onAutomaticSuggestionButtonClicked = () => {
		if (userSellVehicles) {
			const yearlySaving = simulation?.savings.yearlySavings.find(({ year }) => year === selectedYear);
			const ids = yearlySaving?.vehiclesElectrified?.map(({ id }) => id) || [];
			ids?.length &&
				RxUtils.promisify(electrificationWizardService.create(ids), () => {
					notificationService.sendSuccess(t('wizard.create-success'));
					navigate('../processing');
				});
		} else if (userSellInfra) {
			navigate('../processing');
		}
	};

	useEffect(() => {
		(loc.state as any)?.auto && isAutomaticSuggestionAvailable() && onAutomaticSuggestionButtonClicked();
	}, [simulation, selectedYear]);

	return (
		<Stack
			direction="column"
			alignItems="center"
			justifyContent="space-between"
			className={styles.WizardLandingLayout}
			sx={{ backgroundColor: CUSTOM_THEME.extraColors.nightBlue, color: 'common.white' }}
		>
			<Stack direction="column" spacing={11} alignItems="center">
				<Typography variant="h1" sx={{ fontFamily: 'DancingScript', fontWeight: 'bold' }}>
					<Tr.Portal path="wizard.landing.title" />
				</Typography>
				<Typography variant="h4" sx={{ fontWeight: 'bold', maxWidth: 480, textAlign: 'center' }}>
					<Tr.Portal path="wizard.landing.subtitle" />
				</Typography>
			</Stack>
			<Stack direction="column" alignItems="center" spacing={3}>
				<Button
					data-testid="electrification-wizard-automatic-suggestion-button"
					variant="contained"
					color="primary"
					disabled={!isAutomaticSuggestionAvailable()}
					onClick={() => onAutomaticSuggestionButtonClicked()}
				>
					<Tr.Portal path="wizard.landing.automatic-suggestion" />
				</Button>
				{userSellVehicles ? (
					<>
						<Typography sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
							<Tr.Portal path="or" />
						</Typography>
						<RouterLinkButton data-testid="wizard-manual-selection-navigation-button" href="../select-vehicles" variant="light">
							<Tr.Portal path="wizard.landing.manual-selection" />
						</RouterLinkButton>
					</>
				) : null}
			</Stack>
		</Stack>
	);
};
